import { SupabaseClient } from '@supabase/supabase-js';
import camelCase from 'camelcase-keys';
import { z } from 'zod';

import { Database, isFiniteNumber } from '@core';

export type NotificationUiModel = {
    entityId: number | null;
    linkNodeId: string | null;
    entityType: string;
    description: string;
    createdAt: Date;
    createdBy: string;
    swingActivitySequence: number;
    swingCreatedAt: Date;
    swingUuid?: string;
    activityId: number;
    id: number;
    audioUrl?: string;
    readAt?: Date;
    hasDrawing?: boolean;
    sourceUserId?: string | null;
    threadId: number;
};

export type Notification = {
    createdAt: string;
    entityId: number | null;
    entityType: string;
    id: number;
    readAt?: string | null;
    userId: string;
    sourceUserId: string | null;
};

export type ThreadMessageUnreadNotifications = {
    activities: Record<number, number[]>;
    swings: Record<string, number[]>;
    threads: Record<number, number[]>;
    sourceUsers: Record<string, number[]>;
};

export const swingNotificationSchema = z.object({
    id: z.number(),
    message: z.string(),
    createdAt: z.string(),
    createdBy: z.string(),
    swingThreads: z.object({
        id: z.number(),
        swingUuid: z.string(),
        linkNodeId: z.string().nullable(),
        swings: z.object({
            uuid: z.string(),
            activityId: z.number(),
            activitySequence: z.number(),
            createdAt: z.string(),
        }),
    }),
});
export const NOTIFICATION_PAGE_SIZE = 6;

type SwingJoinSchema = z.infer<typeof swingNotificationSchema>;

const JOIN_SQL_SWINGS = `
    id,
    message,
    created_at,
    created_by,
    swing_threads:thread_id (
      id,
      swing_uuid,
      link_node_id,
      swings:swing_uuid (
        uuid,
        activity_id,
        activity_sequence,
        created_at
      )
    )
` as const;

export async function getPaginatedNotifications(
    supabase: SupabaseClient<Database>,
    page = 0,
    sourceUserId: string,
): Promise<Notification[]> {
    const from = page * NOTIFICATION_PAGE_SIZE;
    const to = from + NOTIFICATION_PAGE_SIZE - 1;
    const { data: notifications, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('source_user_id', sourceUserId)
        .order('created_at', { ascending: false })
        .range(from, to);
    if (error) {
        console.error(error);
        throw error;
    }
    return camelCase(notifications);
}

export async function markNotificationsAsRead(supabase: SupabaseClient<Database>, notificationIds?: number[]) {
    const query = supabase.from('notifications').update({ read_at: new Date().toISOString() });

    if (notificationIds) {
        query.in('id', notificationIds);
    } else {
        query.is('read_at', null);
    }

    const { data, error } = await query.select();

    if (error) throw error;
    return data;
}

/**
 * We start off by only supporting swing threads
 * @param supabase the shared supabase client
 */
export async function getUINotification(
    supabase: SupabaseClient<Database>,
    notification: Notification,
): Promise<NotificationUiModel> {
    if (!isFiniteNumber(notification.entityId)) {
        throw new Error('getUiNotification, no entity id set');
    }
    const { data: notificationJoin, error } = await supabase
        .from('swing_thread_messages')
        .select(JOIN_SQL_SWINGS)
        .eq('id', notification.entityId)
        .single();
    if (error) throw error;

    return mapToFullNotification(
        swingNotificationSchema.parse(camelCase(notificationJoin, { deep: true })),
        notification,
    );
}

export async function getThreadMessagesUnreadNotifications(
    supabase: SupabaseClient,
): Promise<ThreadMessageUnreadNotifications> {
    const { data, error } = await supabase.rpc('get_thread_message_unread_notifications');
    if (error) throw error;
    const activities = data?.activities ?? [];
    const swings = data?.swings ?? [];
    const threads = data?.threads ?? [];
    const users = data?.sourceUsers ?? [];

    return {
        activities: Object.fromEntries(
            activities.map((activity: { activityId: number; notificationIds: number[] }) => [
                activity.activityId,
                activity.notificationIds,
            ]),
        ),
        swings: Object.fromEntries(
            swings.map((swing: { swingUuid: string; notificationIds: number[] }) => [
                swing.swingUuid,
                swing.notificationIds,
            ]),
        ),
        threads: Object.fromEntries(
            threads.map((thread: { threadId: number; notificationIds: number[] }) => [
                thread.threadId,
                thread.notificationIds,
            ]),
        ),
        sourceUsers: Object.fromEntries(
            users.map((user: { userId: string; notificationIds: number[] }) => [user.userId, user.notificationIds]),
        ),
    };
}

export const subscribeToNotifications = (
    supabaseClient: SupabaseClient<Database>,
    handler: (payload: any) => Promise<void>,
) => {
    return supabaseClient
        .channel(`notifications_channel`)
        .on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'notifications',
            },
            handler,
        )
        .on(
            'postgres_changes',
            {
                event: 'DELETE',
                schema: 'public',
                table: 'notifications',
            },
            handler,
        )
        .subscribe((status) => {
            if (status === 'CHANNEL_ERROR') {
                console.error('Notifications subscription error:', status);
            }
        });
};

const mapToFullNotification = (schema: SwingJoinSchema, notification: Notification): NotificationUiModel => {
    const { message, swingThreads: thread } = schema;
    const { swings: swing } = thread;

    return {
        sourceUserId: notification.sourceUserId,
        entityId: notification.entityId,
        entityType: notification.entityType,
        description: message,
        createdAt: new Date(notification.createdAt),
        createdBy: schema.createdBy,
        linkNodeId: schema.swingThreads.linkNodeId,
        readAt: notification.readAt ? new Date(notification.readAt) : undefined,
        id: notification.id,
        activityId: swing.activityId,
        swingUuid: swing.uuid,
        swingActivitySequence: swing.activitySequence,
        swingCreatedAt: new Date(swing.createdAt),
        threadId: thread.id,
    };
};
